import { Plus, Users, Shield } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SettingsListLoading } from '@/components/SettingsListLoading';
import { SettingsBreadcrumb } from '@/components/SettingsBreadcrumb';
import { useFetchTeamMembers, useFetchRoles } from '@/hooksApi';
import { TeamMemberRow } from './TeamMemberRow';
import { RoleRow } from './RoleRow';
import { ROUTE } from '@/config';
import RoleFormInline from './RoleFormInline';
import { Role } from '@/types';

const SettingsTeamMembers = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<'users' | 'roles'>('users');
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [editingRoleUID, setEditingRoleUID] = useState('');
  const [editingRoleData, setEditingRoleData] = useState<Role | undefined>(undefined);

  const { data: teamMemberData, isLoading: isLoadingMembers } = useFetchTeamMembers();
  const { data: roleData, isLoading: isLoadingRoles } = useFetchRoles();

  const members = teamMemberData?.object || [];
  const roles = roleData?.object || [];

  const hasOwnerRole = (member: any) => {
    return member.roles?.some((r: any) => r.name.toLowerCase() === 'owner');
  };

  const isAdminRole = (role: any) => {
    return role.name.toLowerCase() === 'admin';
  };

  const getRoleLabel = (member: any) => {
    if (!member.roles || member.roles.length === 0) return t('no_role');
    return member.roles.map((r: any) => r.name).join(', ');
  };

  const handleCreateUser = () => {
    navigate(ROUTE.SETTINGS_TEAM_MEMBERS_CREATE);
  };

  const handleEditUser = (member: any) => {
    navigate(ROUTE.SETTINGS_TEAM_MEMBERS_EDIT.replace(':id', member.uid));
  };

  const handleCreateRole = () => {
    setEditingRoleData(undefined);
    setEditingRoleUID('');
    setShowRoleForm(true);
  };

  const handleEditRole = (role: Role) => {
    setEditingRoleData(role);
    setEditingRoleUID(role.uid);
    setShowRoleForm(true);
  };

  const handleRoleFormClose = () => {
    setShowRoleForm(false);
    setEditingRoleUID('');
    setEditingRoleData(undefined);
  };

  const showingForm = activeTab === 'roles' && showRoleForm;

  return (
    <div className='sd-scope sd-font flex flex-col'>
      <div className='p-6 space-y-6 max-w-3xl w-full mx-auto'>
        <SettingsBreadcrumb moduleName='team_members' moduleRoute={ROUTE.SETTINGS_TEAM_MEMBERS} />

        <div className='flex items-center justify-between'>
          <h1 className='text-xl font-semibold text-sd-foreground m-0'>{t('team_members')}</h1>
          {!showingForm && (
            <button
              onClick={activeTab === 'users' ? handleCreateUser : handleCreateRole}
              className='flex items-center gap-2 px-4 py-2.5 rounded-lg bg-sd-primary text-sd-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity'
            >
              <Plus className='h-4 w-4' />
              {activeTab === 'users' ? t('add_member') : t('add_role')}
            </button>
          )}
        </div>

        {/* Tabs - hidden when form is open */}
        {!showingForm && (
          <div className='flex border-b border-sd-border'>
            <button
              onClick={() => setActiveTab('users')}
              className={`px-5 py-2.5 text-sm font-medium transition-colors relative ${
                activeTab === 'users'
                  ? 'text-sd-foreground'
                  : 'text-sd-muted-foreground hover:text-sd-foreground'
              }`}
            >
              {t('users')}
              {activeTab === 'users' && (
                <span className='absolute bottom-0 left-0 right-0 h-[3px] bg-sd-primary rounded-t-full' />
              )}
            </button>
            <button
              onClick={() => setActiveTab('roles')}
              className={`px-5 py-2.5 text-sm font-medium transition-colors relative ${
                activeTab === 'roles'
                  ? 'text-sd-foreground'
                  : 'text-sd-muted-foreground hover:text-sd-foreground'
              }`}
            >
              {t('roles')}
              {activeTab === 'roles' && (
                <span className='absolute bottom-0 left-0 right-0 h-[3px] bg-sd-primary rounded-t-full' />
              )}
            </button>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' &&
          (isLoadingMembers ? (
            <SettingsListLoading />
          ) : members.length === 0 ? (
            <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm p-8 text-center'>
              <Users className='h-10 w-10 mx-auto text-sd-muted-foreground mb-3' />
              <p className='text-sm text-sd-muted-foreground'>{t('no_team_members_yet')}</p>
            </div>
          ) : (
            <div className='space-y-3'>
              {members.map((m: any) => (
                <TeamMemberRow
                  key={m.uid}
                  member={m}
                  onEdit={handleEditUser}
                  getRoleLabel={getRoleLabel}
                  hasOwnerRole={hasOwnerRole}
                />
              ))}
            </div>
          ))}

        {/* Roles Tab - List */}
        {activeTab === 'roles' &&
          !showRoleForm &&
          (isLoadingRoles ? (
            <SettingsListLoading />
          ) : roles.length === 0 ? (
            <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm p-8 text-center'>
              <Shield className='h-10 w-10 mx-auto text-sd-muted-foreground mb-3' />
              <p className='text-sm text-sd-muted-foreground'>{t('no_roles_yet')}</p>
            </div>
          ) : (
            <div className='space-y-3'>
              {roles.map((r: any) => (
                <RoleRow key={r.uid} role={r} onEdit={handleEditRole} isAdminRole={isAdminRole} />
              ))}
            </div>
          ))}

        {/* Roles Tab - Inline Form (Add / Edit) */}
        {activeTab === 'roles' && showRoleForm && (
          <RoleFormInline
            roleUID={editingRoleUID}
            roleData={editingRoleData}
            onClose={handleRoleFormClose}
          />
        )}
      </div>
    </div>
  );
};

export default SettingsTeamMembers;
